import { useMemo } from 'react';

import { logger } from '@breathelife/monitoring-frontend';
import {
  DEFAULT_TIMEZONE_NAME,
  InsuranceModules,
  PivotAnswerResolver,
  QuestionnaireEngine,
  RenderingQuestionnaire,
  RenderingType,
  Timezone,
  VersionedAnswers,
} from '@zinnia/form-engine-sdk';

import { getQuestionnaireEngineConfig } from '../../Helpers/questionnaireEngineConfigs';
import { useCarrierContext } from '../../Hooks';
import { ApplicationHookProps } from './types';

export function useNeedsAnalysisQuestionnaire(props: ApplicationHookProps): RenderingQuestionnaire | undefined {
  const { features } = useCarrierContext();

  const { application, questionnaire, nodeIdToAnswerPathMap, blueprint } = props;

  const questionnaireEngine = useMemo(() => {
    if (!application || !questionnaire || !nodeIdToAnswerPathMap || !blueprint) return;

    const timezoneResult = Timezone.from(application.timezone || DEFAULT_TIMEZONE_NAME);
    if (timezoneResult.success === false) {
      return;
    }

    const answerResolver = PivotAnswerResolver.from(
      new VersionedAnswers({ v1: application.answers, v2: application.answersV2 }),
      nodeIdToAnswerPathMap,
      blueprint,
      'v1',
      logger,
    );

    return QuestionnaireEngine.from({
      blueprint,
      questionnaire,
      answerResolver,
      context: {
        insuranceModules: [InsuranceModules.needsAnalysis],
      },
      config: getQuestionnaireEngineConfig(application.mode, features),
      timezone: timezoneResult.value,
      applicationContext: application.applicationContext,
      language: application.lang,
      renderingOptions: {
        renderingType: RenderingType.proSummary,
        displayErrors: false,
      },
      currentDateOverride: application.submissionDate?.toString() || null,
    });
  }, [application, nodeIdToAnswerPathMap, questionnaire]);

  return questionnaireEngine?.renderingQuestionnaire;
}
